// src/components/SharePointSearch/SharePointProcessingSection.tsx

import * as React from "react";
import { useState, useEffect } from "react";
import { SharePointToolInput } from "../../interfaces/sharepointSearchTypes";
import { SharePointLogoIcon, DownloadIcon } from "./SharePointIcons";
import { spColors } from "./sharepointSearchStyles";

interface SharePointProcessingSectionProps {
  isProcessingTool: boolean;
  startTime?: number;
  toolInput?: SharePointToolInput;
}

/**
 * Action-aware processing indicator for the SharePoint search tool.
 * Shows different branded bars based on the detected action (search vs read).
 * Progressive delay messages update based on elapsed time.
 */
const SharePointProcessingSection: React.FC<SharePointProcessingSectionProps> = ({
  isProcessingTool,
  startTime,
  toolInput,
}) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isProcessingTool || !startTime) {
      setElapsed(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isProcessingTool, startTime]);

  if (!isProcessingTool) return null;

  const getStatusText = (): string => {
    if (!toolInput) {
      if (elapsed >= 15) return "Taking longer than usual...";
      if (elapsed >= 5) return "Still connecting...";
      return "Connecting to SharePoint...";
    }

    if (toolInput.action === "search") {
      const queryLabel = toolInput.query ? ` for '${toolInput.query}'` : "";
      if (elapsed >= 15) return "Still searching... taking longer than usual";
      if (elapsed >= 5) return "Searching... (this may take a moment)";
      return `Searching SharePoint${queryLabel}...`;
    }

    // read action
    if (elapsed >= 15) return "Still processing... large documents take longer";
    if (elapsed >= 5) return "Downloading content...";
    return "Fetching document...";
  };

  const isReadAction = toolInput?.action === "read";

  return (
    <div style={{ marginBottom: "8px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 12px",
          backgroundColor: spColors.mutedBg,
          borderRadius: "8px",
          border: `1px solid ${spColors.border}`,
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "22px",
            height: "22px",
            flexShrink: 0,
          }}
        >
          {isReadAction ? <DownloadIcon /> : <SharePointLogoIcon size={22} />}
        </div>

        <span style={{ fontSize: "13px", color: spColors.headerText, fontWeight: 500 }}>
          {getStatusText()}
        </span>

        {/* Animated dots */}
        <svg width="20" height="6" viewBox="0 0 20 6" style={{ flexShrink: 0 }}>
          <style>
            {`
              @keyframes sp-dot {
                0%, 80%, 100% { opacity: 0.2; }
                40% { opacity: 1; }
              }
              .sp-dot-1 { animation: sp-dot 1.4s ease-in-out infinite; }
              .sp-dot-2 { animation: sp-dot 1.4s ease-in-out infinite 0.2s; }
              .sp-dot-3 { animation: sp-dot 1.4s ease-in-out infinite 0.4s; }
            `}
          </style>
          <circle className="sp-dot-1" cx="3" cy="3" r="2.5" fill={spColors.teal} />
          <circle className="sp-dot-2" cx="10" cy="3" r="2.5" fill={spColors.teal} />
          <circle className="sp-dot-3" cx="17" cy="3" r="2.5" fill={spColors.teal} />
        </svg>
      </div>
    </div>
  );
};

export default SharePointProcessingSection;
